import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, Button, Group, Loader, Stack, Text } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconMessageCircle } from "@tabler/icons-react";

import dayjs, { Dayjs } from "dayjs";

import { Booking, useBookings } from "@/api/bookings";
import {
  CHAT_PREFILL_EVENT,
  ChatPrefillDetail,
  colors,
  MIN_NIGHTS,
} from "@/constants";
import { trackEvent } from "@/tracking";

import "@mantine/dates/styles.css";

const LOADING_ID = "availability-loading";
const ERROR_ID = "availability-error";

type Range = [string | null, string | null];

export function AvailabilityCalendar() {
  const { t } = useTranslation();
  const { data: unavailableDates, loading, error } = useBookings();
  const [range, setRange] = useState<Range>([null, null]);

  useEffect(() => {
    if (loading) {
      notifications.show({
        id: LOADING_ID,
        message: t("common.api.loading.availibility"),
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    } else {
      notifications.hide(LOADING_ID);
      if (error) {
        notifications.show({
          id: ERROR_ID,
          color: "red",
          message: t("common.api.failed"),
          autoClose: 5000,
        });
      }
    }
  }, [loading, error, t]);

  const isDateDisabled = (dateString: string) => {
    const date = dayjs(dateString);
    if (date.isBefore(dayjs().endOf("day"))) return true;
    return unavailableDates.some(
      (booking) =>
        date.isAfter(booking.start.subtract(1, "day")) &&
        date.isBefore(booking.end),
    );
  };

  const renderDay = (dateString: string) => {
    const date = dayjs(dateString);
    const month = date.month();
    const isOffSeason = month < 4 || month > 8;
    const disabled = isDateDisabled(dateString);
    const accent = isOffSeason ? colors.blue : colors.yellow;

    return (
      <div style={{ color: disabled ? undefined : accent, fontWeight: 600 }}>
        {date.date()}
      </div>
    );
  };

  const selection = useMemo(
    () => evaluateSelection(range, unavailableDates),
    [range, unavailableDates],
  );

  useEffect(() => {
    if (selection.status === "incomplete") return;
    const props: Record<string, string | number> = {
      status: selection.status,
    };
    if (selection.status === "tooShort" || selection.status === "valid") {
      props.nights = selection.nights;
    }
    if (selection.status === "valid") {
      props.startMonth = selection.start.format("YYYY-MM");
    }
    trackEvent("Calendar Range Selected", props);
  }, [selection]);

  const handleAskHosts = () => {
    if (selection.status !== "valid") return;
    const prompt = t("chat.bookingPrefill", {
      start: selection.start.format("MMM D, YYYY"),
      end: selection.end.format("MMM D, YYYY"),
      nights: selection.nights,
    });
    trackEvent("Ask Hosts Click", {
      nights: selection.nights,
      startMonth: selection.start.format("YYYY-MM"),
    });
    window.dispatchEvent(
      new CustomEvent<ChatPrefillDetail>(CHAT_PREFILL_EVENT, {
        detail: { prompt },
      }),
    );
  };

  if (loading) {
    return (
      <Stack align="center" gap="sm" py="lg">
        <Loader color="sea" />
        <Text size="sm" c="dimmed">
          {t("common.api.loading.availibility")}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md" align="center">
      <Text size="sm" c="dimmed">
        {t("bookingPage.minStay", { count: MIN_NIGHTS })}
      </Text>
      <DatePicker
        type="range"
        numberOfColumns={1}
        size="md"
        value={range}
        onChange={(value) => setRange(value as Range)}
        minDate={dayjs().add(1, "day").toDate()}
        maxDate={dayjs().add(1, "year").endOf("year").toDate()}
        excludeDate={isDateDisabled}
        renderDay={renderDay}
        allowSingleDateInRange
      />

      {selection.status === "tooShort" && (
        <Alert
          color="sea"
          variant="light"
          icon={<IconAlertCircle size={18} />}
          w="100%"
          maw={420}
        >
          {t("bookingPage.tooShort", {
            count: MIN_NIGHTS,
            nights: selection.nights,
          })}
        </Alert>
      )}

      {selection.status === "overlap" && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={18} />}
          w="100%"
          maw={420}
        >
          {t("bookingPage.overlap")}
        </Alert>
      )}

      {selection.status === "valid" && (
        <Stack gap="xs" align="center">
          <Text fw={600}>
            {t("bookingPage.selectedRange", {
              nights: selection.nights,
              start: selection.start.format("MMM D"),
              end: selection.end.format("MMM D, YYYY"),
            })}
          </Text>
          <Group justify="center" gap="sm">
            <Button
              color="sea"
              size="md"
              leftSection={<IconMessageCircle size={18} />}
              onClick={handleAskHosts}
            >
              {t("bookingPage.askHosts")}
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}

type SelectionState =
  | { status: "incomplete" }
  | { status: "tooShort"; nights: number }
  | { status: "overlap" }
  | { status: "valid"; start: Dayjs; end: Dayjs; nights: number };

function evaluateSelection(range: Range, bookings: Booking[]): SelectionState {
  const [rawStart, rawEnd] = range;
  if (!rawStart || !rawEnd) return { status: "incomplete" };

  const start = dayjs(rawStart).startOf("day");
  const end = dayjs(rawEnd).startOf("day");
  const nights = end.diff(start, "day");
  if (nights < MIN_NIGHTS) return { status: "tooShort", nights };

  const overlaps = bookings.some(
    (b) => start.isBefore(b.end) && b.start.isBefore(end),
  );
  if (overlaps) return { status: "overlap" };

  return { status: "valid", start, end, nights };
}

export default AvailabilityCalendar;
